import { Component, OnInit, OnDestroy, ViewChildren, QueryList } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PasswordStrengthComponent } from '../../password-strength/password-strength.component';
import { InputComponent } from '../../input/input.component';
import { ButtonComponent } from '../../button/button.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Router, RouterModule } from '@angular/router';
import { LanguageRoutingService } from '../../../services/language-routing.service';
import { AuthService } from '../../../services/auth.service';
import { finalize } from 'rxjs/operators';
import { CardComponent } from '../../card/card.component';
import { InputCheckboxComponent } from '../../input-checkbox/input-checkbox.component';
import { AlertCardComponent } from '../../alert-card/alert-card.component';
import { ModifyUserFromBE } from '../../../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { GoogleAuthService } from '../../../services/google-auth.service';
import { Subscription } from 'rxjs';
import { GoogleAuthProvider, getAuth, signInWithPopup } from 'firebase/auth';
import { GoogleRegister } from '../../../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    ButtonComponent,
    TranslocoModule,
    RouterModule,
    CardComponent,
    InputCheckboxComponent,
    PasswordStrengthComponent,
    AlertCardComponent,
  ],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  providers: [LanguageRoutingService],
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm: FormGroup;
  isSubmitting = false;
  registerError = '';
  showPassword = false;
  formSubmitted = false; // Track if form was submitted to show validation errors
  showConfirmPassword = false;
  registrationSuccess = false;
  isGoogle = false;
  firebaseId = '';
  isGoogleRegistration = false; // Track if registration was via Google SSO
  private googleSubscription?: Subscription;

  // Access all input components to manually mark them as touched
  @ViewChildren(InputComponent) inputComponents!: QueryList<InputComponent>;

  // Current password value for the password strength component
  currentPassword = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private transloco: TranslocoService,
    public languageRoutingService: LanguageRoutingService,
    private googleAuthService: GoogleAuthService
  ) {
    this.registerForm = this.fb.group(
      {
        firstName: ['', [Validators.required]],
        lastName: ['', [Validators.required]],
        email: ['', [Validators.required, Validators.email]],
        cityCountry: [''],
        password: ['', [Validators.required, Validators.minLength(6), PasswordStrengthComponent.passwordStrengthValidator]],
        confirmPassword: ['', [Validators.required]],
        inviteCode: [''],
        aiMatching: [false],
        newsletter: [false],
        termsAccepted: [false, [Validators.requiredTrue]],
      },
      {
        validators: this.passwordMatchValidator,
      }
    );
  }

  ngOnInit(): void {
    // Check if user is already logged in
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['']);
    }

    // Subscribe to Google user data
    this.googleSubscription = this.googleAuthService.currentGoogleUser$.subscribe((data) => {
      if (data) {
        this.isGoogle = data.isGoogle;
        this.firebaseId = data.uid;

        // Split name into firstName and lastName
        const parts: string[] = (data.name || '').trim().split(/\s+/);
        const firstName: string = parts[0] || '';
        const lastName: string = parts.slice(1).join(' ') || '';

        // Pre-fill form with Google data
        this.registerForm.patchValue({
          firstName: firstName,
          lastName: lastName,
          email: data.email,
        });

        // Disable email field so user cannot change it
        this.registerForm.controls['email'].disable();

        // Clear password validators for Google users
        if (this.isGoogle) {
          this.registerForm.controls['password'].clearValidators();
          this.registerForm.controls['confirmPassword'].clearValidators();
          this.registerForm.controls['password'].updateValueAndValidity();
          this.registerForm.controls['confirmPassword'].updateValueAndValidity();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.googleSubscription) {
      this.googleSubscription.unsubscribe();
    }
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;

    if (password !== confirmPassword) {
      form.get('confirmPassword')?.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    return null;
  }

  // Password strength checking is now handled by the PasswordStrengthComponent

  getErrorMessage(controlName: string): string {
    const control = this.registerForm.get(controlName);
    if (!control) return '';

    // Only show errors if field is touched OR form was submitted
    if (!control.touched && !this.formSubmitted) return '';

    if (control.hasError('required')) {
      return this.transloco.translate('auth.validation.required');
    }

    if (control.hasError('email')) {
      return this.transloco.translate('auth.validation.email');
    }

    if (control.hasError('minlength')) {
      return this.transloco.translate('auth.validation.minLength', { count: control.getError('minlength').requiredLength });
    }

    if (control.hasError('weakPassword')) {
      return this.transloco.translate('auth.register.passwordStrength.requirements');
    }

    if (control.hasError('passwordMismatch')) {
      return this.transloco.translate('auth.validation.passwordMismatch');
    }

    if (control.hasError('requiredTrue')) {
      return this.transloco.translate('auth.validation.acceptTerms');
    }

    return '';
  }

  onSubmit(): void {
    this.formSubmitted = true; // Mark form as submitted to show all errors

    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();

      // Also mark input components' internal touched state
      this.inputComponents?.forEach((input: InputComponent) => input.markAsTouched());

      return;
    }

    this.isSubmitting = true;
    this.registerError = '';

    const registrationData: any = {
      firstName: this.registerForm.get('firstName')?.value,
      lastName: this.registerForm.get('lastName')?.value,
      email: this.registerForm.get('email')?.value || this.registerForm.get('email')?.getRawValue(), // getRawValue() for disabled email field
      password: this.registerForm.get('password')?.value,
      mlmInviteCode: this.registerForm.get('inviteCode')?.value,
      mlmInvitedByUserId: 0,
      aiMatching: this.registerForm.get('aiMatching')?.value,
      marketing: this.registerForm.get('newsletter')?.value,
      privacyPolicy: this.registerForm.get('termsAccepted')?.value,
      language: localStorage.getItem('selectedLanguage') || 'en',
      shortcv: {
        cityCountry: this.registerForm.get('cityCountry')?.value,
        questions: {
          age: '',
          jobType: [''],
          educationAchieved: '',
          jobIndustry: [''],
          selfDescription: '',
        },
      },
    };

    // Add Google-specific fields if user registered via Google SSO
    if (this.isGoogle && this.firebaseId) {
      registrationData.firebaseId = this.firebaseId;
      registrationData.isGoogle = true;
      this.isGoogleRegistration = true; // Track that this is a Google registration
    }

    this.authService
      .registerCandidate(registrationData)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (res) => {
          if (res) {
            this.registrationSuccess = true;
          }
        },
        error: (error) => {
          console.log(error);

          let cancel;
          // this.transloco.selectTranslate('shared.cancel', { value: '' }).subscribe((resp: string) => {
          //   cancel = resp;
          // });
          // if (error === 'questions.error') {
          //   this.transloco.selectTranslate(error, { value: '' }).subscribe((resp: string) => {
          //     this.registerError = `Email: ${registrationData.email} ${resp}`;
          //   });
          // } else if (error === 'questions.noMlmInviteCode') {
          //   this.transloco.selectTranslate(error + '1', { value: '' }).subscribe((res: string) => {
          //     res;
          //     this.transloco.selectTranslate(error + '2', { value: '' }).subscribe((resp: string) => {
          //       this.registerError = `${res} ${registrationData.mlmInviteCode} ${resp}`;
          //     });
          //   });
          // } else
          if (error.error?.message === 'EMAIL_EXIST') {
            this.transloco.selectTranslate('auth.register.emailExists').subscribe((message: string) => {
              this.registerError = message;
            });
          } else {
            this.transloco.selectTranslate('auth.register.error').subscribe((message: string) => {
              this.registerError = message;
            });
          }
        },
      });
  }

  // Toggle password visibility
  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  // Toggle confirm password visibility
  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  googleAuth = async (): Promise<any> => {
    const provider = new GoogleAuthProvider();
    const auth = getAuth();
    signInWithPopup(auth, provider)
      .then((result) => {
        const user = result.user;
        let newUser = new GoogleRegister();
        newUser.isGoogle = true;
        newUser.name = user.displayName || '';
        newUser.email = user.email || '';
        newUser.emailVerified = user.emailVerified;
        newUser.uid = user.uid;

        if (user.emailVerified == true) {
          user.getIdToken().then((idToken) => {
            // Send Google user data to be used for pre-filling the form
            this.googleAuthService.sendGoogleUserData(newUser);

            // Check if user is already registered
            this.authService.googleRegister(newUser).subscribe({
              next: (res) => {
                if (res === 'NOT_REGISTERED') {
                  // User not registered, form will be pre-filled via subscription
                  // Just stay on register page
                } else if (res === 'REGISTERED') {
                  // User already registered, log them in
                  this.authService.login(idToken).subscribe({
                    next: (res) => {
                      if (res) {
                        const currentLang = this.languageRoutingService.getCurrentLanguage();
                        this.router.navigate([`/${currentLang}`]);
                      }
                    },
                    error: (error: HttpErrorResponse) => {
                      this.transloco.selectTranslate('auth.login.error').subscribe((message: string) => {
                        this.registerError = message;
                      });
                    },
                  });
                }
              },
              error: (error) => {
                this.transloco.selectTranslate('auth.register.error').subscribe((message: string) => {
                  this.registerError = message;
                });
              },
            });
          });
        }
      })
      .catch((error) => {
        this.transloco.selectTranslate('auth.register.error').subscribe((message: string) => {
          this.registerError = message;
        });
      });
  };
}
